<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent fullscreen scrollable)
  v-card.neutral200
    v-card-text.neutral700--text
      template(v-if="isFetchingPatient || isFetchingHistory")
        v-skeleton-loader(type="list-item-three-line@5")
      template(v-else)
        .d-flex.justify-center.my-6
          div(style="width: 60%;")
            div(v-if="patient")
              //- patient detail
              div.d-flex.align-center
                div.text-2xl.font-semibold {{ $t('visit_summary') }}
                v-spacer
                y-button(label="Edit visit" size="sm" underline unset-min-width append-icon="$yzi-edit-05" icon-size="16" icon-color="neutral700" button-class="px-0" @click="editVisit")
              div.d-flex.align-center.mt-4
                v-avatar.mr-3(size="16" color="neutral300")
                  template(v-if="patient.profile_image")
                    v-img(:src="`https://img.yezza.io/50x50/${patient.profile_image.path}`")
                  v-icon(v-else color="neutral500" size="15") $yzi-user-03
                div.text-sm.font-medium.neutral700--text.mr-3 {{ patient.name }}
                div.text-xs.neutral500--text {{ getDobIc(patient) }}
              //- visit detail
            v-card.round-xl.pa-5.mt-4(v-if="history" outlined)
              div.d-flex.align-center
                div.text-xl.font-medium {{ history.record_date | humanizeDate }}, {{ history.record_date | displayTime2 }}
                v-spacer
                template(v-if="history.provider")
                  div.text-xs.font-medium.neutral700--text By
                  y-chip.ml-1(:label="history.provider.name" size="xs" chip-class="pa-1" color="blue")
                    template(#prepend)
                      v-avatar.mr-1(size="16" color="neutral300")
                        template(v-if="history.provider.profile_image")
                          v-img(:src="`https://img.yezza.io/50x50/${history.provider.profile_image.path}`")
                        v-icon(v-else color="neutral500" size="15") $yzi-user-03
              div.mt-5
                //- visit note
                div.text-xs.font-medium.neutral500--text Visit note
                div.text-sm.neutral700--text.mt-1(v-if="history.consultation && history.consultation.case") {{ history.consultation.case }}
                div.text-sm.neutral700--text.mt-1(v-else) -
                //- diagnosis
                div.text-xs.font-medium.neutral500--text.mt-6 Diagnosis
                template(v-if="history.consultation && history.consultation.diagnoses.length")
                  ol.mt-1
                    template(v-for="(diagnose, idx) in history.consultation.diagnoses")
                      li.text-sm.neutral700--text(:key="`dg-${idx}`") {{ diagnose }}
                div.text-sm.neutral700--text.mt-1(v-else) -
                //- vital signs
                div.text-xs.font-medium.neutral500--text.mt-6 Vital signs
                v-card.mt-1(outlined)
                  v-simple-table(dense)
                    thead
                      tr
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") Ht (cm)
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") Wt (kg)
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") Temp (C)
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") BP (mmHg)
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") HR (bpm)
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") SpO2
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") BG mmol/L
                        th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") RR
                    tbody
                      template(v-if="history.vital_signs.length")
                        template(v-for="(vitalsign, idx) in history.vital_signs")
                          tr(:key="`vs-${idx}`")
                            td.text-xs.neutral700--text.px-2 {{ parseFloat(vitalsign.height) || '-' }}
                            td.text-xs.neutral700--text.px-2 {{ parseFloat(vitalsign.weight) || '-' }}
                            td.text-xs.neutral700--text.px-2 {{ vitalsign.temperature ? `${parseFloat(vitalsign.temperature)}°` : '-' }}
                            td.text-xs.neutral700--text.px-2 {{ vitalsign.bp_systolic ? `${vitalsign.bp_systolic}/ ${vitalsign.bp_diastolic}` : '-' }}
                            td.text-xs.neutral700--text.px-2 {{ vitalsign.pulse ? `${vitalsign.pulse }` : '-' }}
                            td.text-xs.neutral700--text.px-2 {{ vitalsign.spo2 ? `${vitalsign.spo2 }%` : '-' }}
                            td.text-xs.neutral700--text.px-2 {{ vitalsign.blood_glucose || '-' }}
                            td.text-xs.neutral700--text.px-2 {{ vitalsign.respiratory_rate || '-' }}
                      template(v-else)
                        tr
                          td.text-xs.neutral700--text.px-2.text-center(colspan="8") No vital signs recorded.
                //- case note
                div.text-xs.font-medium.neutral500--text.mt-6 Case note
                div.text-sm.neutral700--text.mt-1(v-if="history.consultation && history.consultation.note" v-html="history.consultation.note")
                div.text-sm.neutral700--text.mt-1(v-else) -
                //- treatment
                div.text-xs.font-medium.neutral500--text.mt-6 Treatment
                div.mt-1(v-if="history.treatments.length")
                  template(v-for="(treatment, index) in history.treatments")
                    div(:key="`trtm-${index}`")
                      v-divider(v-if="index")
                      div.py-2
                        div.d-flex.align-center
                          div.text-xs.font-semibold.neutral700--text {{ treatment.name }}
                            span.ml-1 x{{ treatment.quantity }}
                          y-chip.ml-2(v-if="checkActiveMadication(treatment)" label="Active" size="xs" chip-class="pa-1" color="green")
                            template(#prepend)
                              v-avatar.mr-1(size="6" color="success500")
                          v-spacer
                          div.text-xs.neutral700--text {{ (treatment.price * treatment.quantity) | displayPrice($currency) }}
                        //- packages children
                        div.mt-1(v-if="treatment.product.type === 'bundle'")
                          template(v-for="(bundle, idx) in treatment.bundles")
                            div.d-flex(:key="`phpckg-${index}-${idx}`")
                              v-icon.ml-2.mr-1(size="14" color="neutral700") $yzi-corner-down-right
                              div
                                div.text-xxs.font-medium.neutral700--text.ellipsis-text {{ bundle.name }}
                                  span.ml-1(v-if="bundle.quantity_per_set > 1") x{{ bundle.quantity_per_set }}
                                div.text-xxs.neutral700--text(v-if="Object.keys(bundle.extra_data).length") {{ getTreatmentExtraData(bundle.extra_data) }}
                        //- extra data for non-package
                        div.text-xs.neutral700--text.mt-1(v-else-if="Object.keys(treatment.extra_data).length") {{ getTreatmentExtraData(treatment.extra_data) }}
                div.mt-1(v-else)
                  div.text-xs.neutral700--text No treatment plan recorded.
                //- uploaded file
                div.text-xs.font-medium.neutral500--text.mt-5 Uploaded file
                div.mt-1(v-if="history.consultation && history.consultation.attachments.length")
                  template(v-for="(attachment, idx) in history.consultation.attachments")
                    div(:key="`attc-${idx}`")
                      v-divider(v-if="idx")
                      div.d-flex.align-center.py-2
                        div.mr-2(v-if="attachment.document.type.includes('image')")
                          v-img(:src="attachment.document.file" width="20" height="20" contain)
                        v-icon.mr-2(v-else-if="attachment.document.type.includes('pdf')" color="neutral500" size="20") mdi-file-pdf-box
                        div.text-xs.font-semibold.neutral700--text {{ attachment.name }} - {{ attachment.document.filename }}
                        v-spacer
                        v-btn.mr-2(icon x-small color="neutral500" @click="deleteItem(attachment)")
                          v-icon(size="16") $yzi-trash-03
                        v-btn.mr-2(icon x-small color="neutral500" @click="downloadFile(attachment)")
                          v-icon(size="16") $yzi-download-01
                        v-btn(icon x-small color="neutral500" @click="viewFile(attachment)")
                          v-icon(size="16") $yzi-eye
                div.mt-1(v-else)
                  div.text-xs.neutral700--text No file uploaded.
                //- appointment
                template(v-if="history.registration && history.registration.appointment")
                  div.text-xs.font-medium.neutral500--text.mt-5 Appointment
                  v-alert.mt-1.mb-0.py-1(border="left" colored-border dense color="purple600" style="background-color: var(--v-neutral200);")
                    div.d-flex.align-center
                      div
                        y-chip(:label="history.registration.appointment.provider.name" size="xs" chip-class="pa-1")
                          template(#prepend)
                            v-avatar.mr-1(size="16" color="neutral300")
                              template(v-if="history.registration.appointment.provider.profile_image")
                                v-img(:src="`https://img.yezza.io/50x50/${history.registration.appointment.provider.profile_image.path}`")
                              v-icon(v-else color="neutral500" size="15") $yzi-user-03
                        div.text-xs.font-medium.neutral700--text.ml-1 {{ getAppointmentServices(history.registration.appointment) }}
                      v-spacer
                      div.text-xs.neutral500--text.no-wrap.ml-2 {{ history.registration.appointment.start | humanizeDate }}

    v-divider
    v-card-actions.px-6.white
      v-spacer
      y-button(label="Close" dark @click="closeDialog")

    .dialogs
      view-image-dialog(v-if="showViewImageDialog" :open.sync="showViewImageDialog" :image="selectedFile" @closed="selectedFile = null")
      delete-attachment-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :pid="history.lead_pid" :file="selectedFile" @deleted="handleDeletedItem" @closed="selectedFile = null")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewImageDialog from '@/components/dialogs/ViewImageDialog.vue'
import DeleteAttachmentDialog from '@/components/clinic/dialogs/DeleteAttachmentDialog.vue'

export default {
  components: { ViewImageDialog, DeleteAttachmentDialog },
  props: {
    open: {
      type: Boolean,
    },
    visit: {
      type: Object,
      default: () => ({}),
      required: true
    },
    patientId: {
      type: Number,
      default: null
    },
  },
  data () {
    return {
      isFetchingHistory: false,
      isFetchingPatient: false,
      isLoading: false,
      addTreatmentMode: false,
      showViewImageDialog: false,
      showDeleteDialog: false,
      hasChanges: false,
      history: null,
      patient: null,
      selectedFile: null,
      activeMedications: [],
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.fetchPatient()
      this.fetchHistory()
    },
    fetchHistory () {
      this.isFetchingHistory = true
      this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patientId}/histories/${this.visit.lead_pid}`)
        .then(resp => {
          this.history = resp.data
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingHistory = false
        })
    },
    fetchPatient () {
      this.isFetchingPatient = true
      this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patientId}`)
        .then(resp => {
          this.patient = resp.data
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPatient = false
        })
    },
    fetchActiveMedication () {
      const params = {
        page_size: -1,
        patient: this.patient.id,
        expand: 'medication'
      }
      this.$axios.get(`/clinics/${this.company.slug}/active_medications`, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.activeMedications = resp.data.results.map(m => { return { id: m.id, m_id: m.medication.id, name: m.medication.name } })
          }
        })
        .catch(() => {})
    },
    editVisit () {
      let params = { pid: this.history.lead_pid, origin: 'ClinicPatientDetails', data: { params: { id: this.patientId }, query: { leadPid: this.history.lead_pid, tab: 'visits' } } }
      this.$router.push({ name: 'ClinicConsultationDetails', params })
    },
    deleteItem (item) {
      this.showDeleteDialog = true
      this.selectedFile = item
    },
    handleDeletedItem () {
      this.selectedFile = null
      this.hasChanges = true
    },
    checkActiveMadication (treatment) {
      if (treatment.product.medication && treatment.product.medication) {
        if (this.activeMedications.find(f => f.m_id === treatment.product.medication.id)) {
          return true
        }
      }
      return false
    },
    downloadFile (item) {
      window.open(item.document.file, '_blank')
    },
    getDobIc (patient) {
      let items = []
      if (patient.dob) {
        let age = `${this.$options.filters.displayAgeByYearMonthDay(this.$options.filters.getAgeByYearMonthDay(patient.dob))} old`
        items.push(age)
      } if (patient.ic) {
        items.push(patient.ic)
      }
      return items.join(' | ')
    },
    getTreatmentExtraData (extra_data) {
      let data = []
      if (extra_data.indication) { data.push(extra_data.indication) }
      if (extra_data.dosage_value && extra_data.dosage_unit) { data.push(`${extra_data.dosage_value} ${extra_data.dosage_unit}`) }
      if (extra_data.frequency) { data.push(extra_data.frequency) }
      if (extra_data.duration) { data.push(extra_data.duration) }
      if (extra_data.instruction) { data.push(extra_data.instruction) }
      if (extra_data.precaution) { data.push(extra_data.precaution) }
      return data.join(' | ')
    },
    getAppointmentServices (appointment) {
      let services = []
      if (appointment.services.length) {
        services = appointment.services.map(s => s.name)
      }
      return services.join(', ')
    },
    viewFile (item) {
      if (item.document.type.includes('image')) {
        this.viewImage(item.document.file)
      } else if (item.document.type.includes('pdf')) {
        this.viewPDF(item.document.file)
      }
    },
    viewImage (image) {
      this.selectedFile = image
      this.showViewImageDialog = true
    },
    viewPDF (file) {
      const url = `https://docs.google.com/viewer?url=${file}`
      window.open(url,'_blank')
    },
    closeDialog() {
      if (this.hasChanges) {
        this.$emit('updated')
      }
      this.$emit('closed')
      this.showDialog = false
    },
  },
}
</script>

<style lang="scss" scoped>
</style>